import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { Package, TrendingUp, AlertTriangle, MapPin, Clock, DollarSign, Truck } from 'lucide-react';
import { 
  MOCK_ASSETS, 
  DASHBOARD_STATS, 
  MOCK_ALERTS, 
  WAREHOUSES,
  DAILY_TRANSPORT_COSTS,
  AGING_THRESHOLDS
} from '../services/mockData';
import 'leaflet/dist/leaflet.css';
import '../styles/Dashboard.css';

// Fix for default marker icon in react-leaflet
import L from 'leaflet';
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import iconRetina from 'leaflet/dist/images/marker-icon-2x.png';

// Custom red marker for warehouses with CRITICAL alerts
const redIcon = L.icon({
  iconUrl: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjUiIGhlaWdodD0iNDEiIHZpZXdCb3g9IjAgMCAyNSA0MSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNMTIuNSAwQzUuNiAwIDAgNS42IDAgMTIuNWMwIDEwLjYgMTIuNSAyOC41IDEyLjUgMjguNVMyNSAyMy4xIDI1IDEyLjVDMjUgNS42IDE5LjQgMCAxMi41IDB6IiBmaWxsPSIjZWY0NDQ0Ii8+PGNpcmNsZSBjeD0iMTIuNSIgY3k9IjEyLjUiIHI9IjYiIGZpbGw9IndoaXRlIi8+PC9zdmc+',
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [0, -41]
});

// Custom orange marker for warehouses with HIGH severity alerts
const orangeIcon = L.icon({
  iconUrl: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjUiIGhlaWdodD0iNDEiIHZpZXdCb3g9IjAgMCAyNSA0MSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNMTIuNSAwQzUuNiAwIDAgNS42IDAgMTIuNWMwIDEwLjYgMTIuNSAyOC41IDEyLjUgMjguNVMyNSAyMy4xIDI1IDEyLjVDMjUgNS42IDE5LjQgMCAxMi41IDB6IiBmaWxsPSIjZjU5ZTBiIi8+PGNpcmNsZSBjeD0iMTIuNSIgY3k9IjEyLjUiIHI9IjYiIGZpbGw9IndoaXRlIi8+PC9zdmc+',
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [0, -41]
});

// Custom blue marker for warehouses with no alerts
const blueIcon = L.icon({
  iconUrl: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjUiIGhlaWdodD0iNDEiIHZpZXdCb3g9IjAgMCAyNSA0MSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNMTIuNSAwQzUuNiAwIDAgNS42IDAgMTIuNWMwIDEwLjYgMTIuNSAyOC41IDEyLjUgMjguNVMyNSAyMy4xIDI1IDEyLjVDMjUgNS42IDE5LjQgMCAxMi41IDB6IiBmaWxsPSIjMzc4MGZmIi8+PGNpcmNsZSBjeD0iMTIuNSIgY3k9IjEyLjUiIHI9IjYiIGZpbGw9IndoaXRlIi8+PC9zdmc+',
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [0, -41]
});

const greenIcon = L.icon({
  iconUrl: icon,
  iconRetinaUrl: iconRetina,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [0, -41]
});

const Dashboard = () => {
  const [stats] = useState(DASHBOARD_STATS);
  const [assets] = useState(MOCK_ASSETS);
  const [alerts] = useState(MOCK_ALERTS);
  const [transportCosts] = useState(DAILY_TRANSPORT_COSTS);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);

  // Calculate warehouse aging violations based on MOCK_ALERTS
  const warehouseViolations = {};
  Object.keys(WAREHOUSES).forEach(key => {
    const warehouseName = WAREHOUSES[key].name;
    const violations = alerts.filter(alert =>
      alert.warehouse === warehouseName
    );
    warehouseViolations[warehouseName] = violations;
  });

  // Get recent assets with transfer activity
  const recentTransfers = assets
    .filter(a => a.transferHistory && a.transferHistory.length > 0)
    .slice(0, 10);

  // Sort alerts by severity
  const severityOrder = { 'CRITICAL': 1, 'HIGH': 2, 'MEDIUM': 3, 'LOW': 4 };
  const sortedAlerts = [...alerts].sort((a, b) =>
    severityOrder[a.severity] - severityOrder[b.severity]
  ).slice(0, 5);

  // Calculate aging and battery alerts separately
  const agingAlerts = alerts.filter(a => a.type === 'AGING').length;
  const batteryAlerts = alerts.filter(a => a.type === 'BATTERY').length;

  // Calculate total daily transport cost
  const totalDailyCost = transportCosts.reduce((sum, item) => sum + item.cost, 0);
  const totalDailyTrips = transportCosts.reduce((sum, item) => sum + item.trips, 0);

  const getSeverityClass = (severity) => {
    return `severity-${severity.toLowerCase()}`;
  };

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1>Leadec Smart Asset Tracking System</h1>
        <p className="dashboard-subtitle">Warehouse Transfer & Aging Monitoring</p>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">
            <Package size={24} />
          </div>
          <div className="stat-content">
            <h3>Total Assets</h3>
            <p className="stat-value">{stats.totalAssets.toLocaleString()}</p>
            <p className="stat-change positive">+{stats.totalAssetsThisMonth} this month</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <Truck size={24} />
          </div>
          <div className="stat-content">
            <h3>In Transit</h3>
            <p className="stat-value">{stats.assetsInTransit.toLocaleString()}</p>
            <p className="stat-meta">60% of total assets</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon alert">
            <AlertTriangle size={24} />
          </div>
          <div className="stat-content">
            <h3>Active Alerts</h3>
            <p className="stat-value">{stats.activeAlerts}</p>
            <p className="stat-meta">{agingAlerts} Aging • {batteryAlerts} Battery</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <TrendingUp size={24} />
          </div>
          <div className="stat-content">
            <h3>Avg Utilization</h3>
            <p className="stat-value">{stats.averageUtilization}%</p>
            <p className="stat-meta">Across all assets</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <DollarSign size={24} />
          </div>
          <div className="stat-content">
            <h3>Daily Transport Cost</h3>
            <p className="stat-value">₹{(totalDailyCost / 1000).toFixed(0)}K</p>
            <p className="stat-meta">{totalDailyTrips} trips today</p>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="dashboard-grid">
        {/* Warehouse Map with Alerts */}
        <div className="dashboard-card map-card">
          <div className="card-header">
            <h2>
              <MapPin size={20} />
              Warehouse Locations - Alert Status
            </h2>
            <p className="card-subtitle">Red: Critical alerts • Orange: High alerts • Blue: No alerts</p>
          </div>
          <div className="map-container">
            <MapContainer 
              center={[18.5204, 73.8567]} 
              zoom={5} 
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              />
              {Object.keys(WAREHOUSES).map(key => {
                const warehouse = WAREHOUSES[key];
                const violations = warehouseViolations[warehouse.name] || [];
                const hasViolations = violations.length > 0;

                // Determine icon color based on highest severity
                const hasCritical = violations.some(v => v.severity === 'CRITICAL');
                const hasHigh = violations.some(v => v.severity === 'HIGH');
                let markerIcon = blueIcon; // Default to blue for no alerts
                if (hasCritical) {
                  markerIcon = redIcon;
                } else if (hasHigh) {
                  markerIcon = orangeIcon;
                }

                return (
                  <Marker
                    key={key}
                    position={[warehouse.lat, warehouse.lng]}
                    icon={markerIcon}
                    eventHandlers={{
                      click: () => setSelectedWarehouse(warehouse.name)
                    }}
                  >
                    <Popup>
                      <div className="warehouse-popup">
                        <h3>{warehouse.name} Warehouse</h3>
                        <p><strong>Code:</strong> {warehouse.code}</p>
                        {hasViolations ? (
                          <div className="violation-info">
                            <p className="violation-count">
                              <AlertTriangle size={16} color={hasCritical ? "#ef4444" : "#f59e0b"} />
                              <strong>{violations.length} alert{violations.length > 1 ? 's' : ''}</strong>
                            </p>
                            <ul className="violation-list">
                              {violations.map(alert => (
                                <li key={alert.id}>
                                  <strong>{alert.assetName}</strong>
                                  {alert.type === 'AGING' ? (
                                    <span> - Aging: {alert.ageDays} days (max: {alert.threshold})</span>
                                  ) : (
                                    <span> - Battery: {alert.batteryLevel}% (min: {alert.threshold}%)</span>
                                  )}
                                  <br/>
                                  <span style={{fontSize: '11px', color: '#6b7280'}}>
                                    Severity: {alert.severity}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ) : (
                          <p className="no-violations">✓ No alerts</p>
                        )}
                      </div>
                    </Popup>
                  </Marker>
                );
              })}
            </MapContainer>
          </div>
        </div>

        {/* Recent Alerts */}
        <div className="dashboard-card alerts-card">
          <div className="card-header">
            <h2>
              <AlertTriangle size={20} />
              Recent Alerts
            </h2>
          </div>
          <div className="alerts-list">
            {sortedAlerts.length === 0 ? (
              <p className="no-data">No active alerts</p>
            ) : (
              sortedAlerts.map(alert => (
                <div key={alert.id} className={`alert-item ${getSeverityClass(alert.severity)}`}>
                  <div className="alert-header">
                    <span className="alert-severity">{alert.severity}</span>
                    <span className="alert-time">
                      {new Date(alert.timestamp).toLocaleTimeString()}
                    </span>
                  </div>
                  <p className="alert-message">{alert.message}</p>
                  <div className="alert-details">
                    <span>{alert.assetName}</span>
                    <span>•</span>
                    <span>{alert.warehouse}</span>
                    {alert.ageDays && (
                      <>
                        <span>•</span>
                        <span>{alert.ageDays} days (max: {alert.threshold})</span>
                      </>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Transportation Costs Today */}
      <div className="dashboard-card">
        <div className="card-header">
          <h2>
            <DollarSign size={20} />
            Transportation Costs - Today
          </h2>
          <p className="card-subtitle">Daily route-wise transportation expenses</p>
        </div>
        <div className="transport-costs-grid">
          {transportCosts.map((item, index) => (
            <div key={index} className="transport-cost-item">
              <div className="route-info">
                <Truck size={18} />
                <span className="route-name">{item.route}</span>
              </div>
              <div className="cost-info">
                <span className="trips-count">{item.trips} trips</span>
                <span className="cost-amount">₹{(item.cost / 1000).toFixed(1)}K</span>
              </div>
            </div>
          ))}
        </div>
        <div className="transport-total">
          <strong>Total Daily Cost:</strong>
          <span className="total-amount">₹{(totalDailyCost / 1000).toFixed(1)}K</span>
          <span className="total-trips">({totalDailyTrips} trips)</span>
        </div>
      </div>

      {/* Recent Asset Transfers */}
      <div className="dashboard-card">
        <div className="card-header">
          <h2>
            <Clock size={20} />
            Recent Asset Transfers
          </h2>
          <p className="card-subtitle">Latest warehouse transfer activity</p>
        </div>
        <div className="transfers-table-container">
          <table className="transfers-table">
            <thead>
              <tr>
                <th>Asset ID</th>
                <th>Type</th>
                <th>Current Location</th>
                <th>Status</th>
                <th>Age (Days)</th>
                <th>Last Transfer</th>
              </tr>
            </thead>
            <tbody>
              {recentTransfers.map(asset => {
                const lastTransfer = asset.transferHistory[asset.transferHistory.length - 1];
                return (
                  <tr key={asset.id} className={asset.exceedsThreshold ? 'exceeds-threshold' : ''}>
                    <td><strong>{asset.id}</strong></td>
                    <td>{asset.type}</td>
                    <td>
                      {asset.currentWarehouse || asset.currentCustomer || 'In Transit'}
                    </td>
                    <td>
                      <span className={`status-badge status-${asset.status.toLowerCase().replace('_', '-')}`}>
                        {asset.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td>
                      {asset.currentAgeDays !== undefined ? (
                        <span className={asset.exceedsThreshold ? 'age-exceeded' : ''}>
                          {asset.currentAgeDays} 
                          {asset.exceedsThreshold && ` (max: ${AGING_THRESHOLDS[asset.type]})`}
                        </span>
                      ) : '-'}
                    </td>
                    <td>
                      {lastTransfer ? new Date(lastTransfer.transferIn).toLocaleDateString() : '-'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;

